import { getFaceMatchingRepository } from '@/modules/attendance/infrastructure/repositories/container';
import type { FaceEmbedding } from '@/modules/attendance/domain/valueObjects/FaceEmbedding';

export interface EnrollDuplicateCheckResult {
  isDuplicate:          boolean;
  conflictingIdPerson?: number;
}

/**
 * Checks whether the given embedding matches an existing employee (other than the
 * current enrollee) above the given threshold.
 *
 * Uses FaceMatchingRepository.match() — 1:N brute-force against all cached embeddings
 * for the access role. If the best match is for a DIFFERENT idPerson → duplicate detected.
 *
 * @param embedding        - The mean embedding computed from all enroll poses.
 * @param accessRoleId     - The access role to search against.
 * @param currentIdPerson  - The enrollee's idPerson (excluded from duplicate detection).
 * @param matchThreshold   - Cosine similarity threshold for match (from quality settings).
 */
export async function checkEnrollDuplicateFace(
  embedding:        FaceEmbedding,
  accessRoleId:     number,
  currentIdPerson:  number,
  matchThreshold:   number,
): Promise<EnrollDuplicateCheckResult> {
  const repo   = getFaceMatchingRepository();
  const result = await repo.match(embedding, accessRoleId, matchThreshold);

  if (result.employee && result.employee.idPerson !== currentIdPerson) {
    return {
      isDuplicate:         true,
      conflictingIdPerson: result.employee.idPerson,
    };
  }

  return { isDuplicate: false };
}
